import { sumBy } from '@markku/utils'
import { z } from 'zod'

export interface ServiceTaskLine {
  taskId: number
  /** Computed price for this booking, in euros. */
  price: number
}

export interface ServiceBookingData {
  tasks: ServiceTaskLine[]
  serviceDetails: string
  estimatedDurationMinutes: number
  estimatedCost: number
}

export type ServicePresetKey =
  | 'nuohous'
  | 'ilmanvaihto'
  | 'ilmanvaihto_v2'
  | 'ilp_huolto'

export interface ServicePresetData {
  id: string
  name: string
  description: string
  maxDrivingMinutes: number
  /**
   * Optional low-season fallback. When a strict search (using maxDrivingMinutes)
   * returns no slots, retry the near term with a looser cap — those slots have
   * low opportunity cost (they'd likely go unfilled anyway), so a slightly longer
   * drive beats offering no slot at all. Each tier is one extra search: `daysAhead`
   * bounds the window to now → end of that day (so today is included, not just
   * tomorrow), and `extraMinutes` is added to the base cap. Windows overlap, so a
   * day's effective cap is the largest bonus among tiers whose window covers it —
   * meaning `extraMinutes` MUST be non-increasing as `daysAhead` grows (sooner =
   * more relaxed), which is also the intent. Only set for tightly-capped services.
   */
  drivingRelaxation?: { daysAhead: number; extraMinutes: number }[]
  minBookingAdvanceHours: number
  slotSpacingMinutes: number
  notificationEmails: string[]
  defaultFormValue: Record<string, unknown>
  getBookingData: (formValue: Record<string, unknown>) => ServiceBookingData
}

// --- Nuohous (chimney sweep) preset data ---

interface NuohousOption {
  fireplaces: number
  taskId: number
  duration: number
  cost: number
}

interface NuohousBoilerOption {
  taskId: number
  duration: number
  cost: number
}

export const nuohousFormValueSchema = z.object({
  fireplaces: z.number().int().min(1).max(5),
  hasBoiler: z.boolean().optional(),
})

export type NuohousFormValue = z.infer<typeof nuohousFormValueSchema>

// Fireplace options: fireplaces, taskId, duration (min), cost (€).
// Task IDs match production — the same IDs are seeded locally via fixtures.
export const NUOHOUS_OPTIONS: NuohousOption[] = [
  { fireplaces: 1, taskId: 1741, duration: 25, cost: 68 },
  { fireplaces: 2, taskId: 54356, duration: 40, cost: 88 },
  { fireplaces: 3, taskId: 54373, duration: 55, cost: 108 },
  { fireplaces: 4, taskId: 54323, duration: 70, cost: 128 },
  { fireplaces: 5, taskId: 54238, duration: 85, cost: 148 },
]

// Boiler sweep add-on: taskId, duration (min), cost (€)
export const NUOHOUS_BOILER_OPTION: NuohousBoilerOption = {
  taskId: 54240,
  duration: 15,
  cost: 25,
}

export function getNuohousBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const { fireplaces, hasBoiler = false } = formValue as NuohousFormValue
  const opt =
    NUOHOUS_OPTIONS.find((o) => o.fireplaces === fireplaces) ??
    NUOHOUS_OPTIONS[0]

  const tasks: ServiceTaskLine[] = [{ taskId: opt.taskId, price: opt.cost }]
  let totalDuration = opt.duration

  if (hasBoiler) {
    tasks.push({
      taskId: NUOHOUS_BOILER_OPTION.taskId,
      price: NUOHOUS_BOILER_OPTION.cost,
    })
    totalDuration += NUOHOUS_BOILER_OPTION.duration
  }

  const totalCost = sumBy(tasks, (t) => t.price)
  const fireplaceText = `${opt.fireplaces} tulisija${opt.fireplaces > 1 ? 'a' : ''}`
  const boilerText = hasBoiler ? ' + kattilanuohous' : ''

  return {
    tasks,
    serviceDetails: `${fireplaceText}${boilerText} - ${totalCost} €`,
    estimatedDurationMinutes: totalDuration,
    estimatedCost: totalCost,
  }
}

// --- Ilmanvaihto (ventilation cleaning) preset data ---

export const apartmentSizeSchema = z.enum([
  'under_80',
  '80_to_120',
  '120_to_160',
])
export type ApartmentSize = z.infer<typeof apartmentSizeSchema>

export const floorsSchema = z.enum(['single', 'multiple'])

export const ventilationTypeSchema = z.enum([
  'natural',
  'mechanical_exhaust',
  'heat_recovery',
])
export type VentilationType = z.infer<typeof ventilationTypeSchema>

export const ilmanvaihtoFormValueSchema = z.object({
  apartmentSize: apartmentSizeSchema,
  floors: floorsSchema,
  ventilationType: ventilationTypeSchema,
  includeAirflowMeasurement: z.boolean().optional(),
  includeCookerHoodCleaning: z.boolean().optional(),
})

export type IlmanvaihtoFormValue = z.infer<typeof ilmanvaihtoFormValueSchema>

// Prices by size × ventilation type
export const IV_CLEANING_PRICES: Record<
  ApartmentSize,
  Record<VentilationType, number>
> = {
  under_80: { natural: 300, mechanical_exhaust: 390, heat_recovery: 450 },
  '80_to_120': {
    natural: 350,
    mechanical_exhaust: 450,
    heat_recovery: 520,
  },
  '120_to_160': {
    natural: 400,
    mechanical_exhaust: 490,
    heat_recovery: 590,
  },
}

// Airflow measurement add-on prices (50% of standalone, varies by size)
export const IV_MEASUREMENT_PRICES: Record<ApartmentSize, number> = {
  under_80: 75,
  '80_to_120': 100,
  '120_to_160': 125,
}

export const IV_MULTI_FLOOR_SURCHARGE = 50
export const IV_COOKER_HOOD_PRICE = 60

// Duration estimates (minutes) — full lookup table (from IV expert)
const IV_DURATION: Record<ApartmentSize, Record<VentilationType, number>> = {
  under_80: { natural: 150, mechanical_exhaust: 150, heat_recovery: 180 },
  '80_to_120': { natural: 180, mechanical_exhaust: 180, heat_recovery: 220 },
  '120_to_160': { natural: 180, mechanical_exhaust: 180, heat_recovery: 270 },
}

const IV_DURATION_MULTI_FLOOR = 15
const IV_DURATION_COOKER_HOOD = 30

const IV_DURATION_MEASUREMENT: Record<
  ApartmentSize,
  Record<'mechanical_exhaust' | 'heat_recovery', number>
> = {
  under_80: { mechanical_exhaust: 20, heat_recovery: 45 },
  '80_to_120': { mechanical_exhaust: 40, heat_recovery: 60 },
  '120_to_160': { mechanical_exhaust: 60, heat_recovery: 100 },
}

// Task IDs match production — the same IDs are seeded locally via fixtures.
export const IV_TASK_IDS: Record<
  VentilationType | 'measurement' | 'cookerHood',
  number
> = {
  natural: 60317,
  mechanical_exhaust: 69448,
  heat_recovery: 69042,
  measurement: 69419,
  cookerHood: 82225,
}

const IV_TYPE_LABELS: Record<VentilationType, string> = {
  natural: 'painovoimainen',
  mechanical_exhaust: 'koneellinen poisto',
  heat_recovery: 'tulo- ja poisto (LTO)',
}

const IV_SIZE_LABELS: Record<ApartmentSize, string> = {
  under_80: 'alle 80 m²',
  '80_to_120': '80–120 m²',
  '120_to_160': '120–160 m²',
}

export function getIlmanvaihtoBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const {
    apartmentSize: size,
    floors,
    ventilationType: ventType,
    includeAirflowMeasurement = false,
    includeCookerHoodCleaning: hasCookerHood = false,
  } = formValue as IlmanvaihtoFormValue
  // Measurement add-on is only valid for non-natural ventilation
  const hasMeasurement = includeAirflowMeasurement && ventType !== 'natural'

  // Main task price = base + multi-floor surcharge folded in (no separate task)
  const mainTaskPrice =
    IV_CLEANING_PRICES[size][ventType] +
    (floors === 'multiple' ? IV_MULTI_FLOOR_SURCHARGE : 0)

  const tasks: ServiceTaskLine[] = [
    { taskId: IV_TASK_IDS[ventType], price: mainTaskPrice },
  ]
  if (hasMeasurement) {
    tasks.push({
      taskId: IV_TASK_IDS.measurement,
      price: IV_MEASUREMENT_PRICES[size],
    })
  }
  if (hasCookerHood) {
    tasks.push({
      taskId: IV_TASK_IDS.cookerHood,
      price: IV_COOKER_HOOD_PRICE,
    })
  }

  // Duration
  let duration = IV_DURATION[size][ventType]
  if (floors === 'multiple') duration += IV_DURATION_MULTI_FLOOR
  if (hasMeasurement)
    duration +=
      IV_DURATION_MEASUREMENT[size][
        ventType as 'mechanical_exhaust' | 'heat_recovery'
      ]
  if (hasCookerHood) duration += IV_DURATION_COOKER_HOOD

  const totalCost = sumBy(tasks, (t) => t.price)

  // Service details string (compact, single line — used in emails and internal notes)
  const parts = [
    `IV-puhdistus, ${IV_TYPE_LABELS[ventType]}, ${IV_SIZE_LABELS[size]}`,
  ]
  if (floors === 'multiple') parts.push('useampi kerros')
  if (hasMeasurement) parts.push('mittaus ja säätö')
  if (hasCookerHood) parts.push('liesituuletin')

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${totalCost} €`,
    estimatedDurationMinutes: duration,
    estimatedCost: totalCost,
  }
}

// --- Ilmanvaihto v2 (granular per-room calculator on markku.fi) ---
//
// The new website calculator prices per room/feature instead of by apartment
// size band, and derives the visit duration directly from the price
// (€/hour → minutes). This formula is the server-authoritative port of the
// client calculator at apps/markku-website .../ilmanvaihtokanavien-puhdistus —
// the website only shows a preview; these numbers are the source of truth.
//
// ⚠️ KEEP IN SYNC: the markku-website repo (separate repo) hardcodes a mirror
// copy of these constants in its IV calculator (index.astro + [city].astro,
// search "ÄLÄ MUOKKAA ILMAN TECH-TIIMIÄ") for the instant preview. If you
// change a price or the duration rule here, update that copy too — otherwise
// the website preview will diverge from the confirmed/invoiced amount.

export const ilmanvaihtoV2FormValueSchema = z.object({
  ventilationType: ventilationTypeSchema,
  floors: z.number().int().min(1).max(5),
  // Rooms (excl. living room & kitchen) — only billed for heat_recovery (LTO)
  bedrooms: z.number().int().min(0).max(7).optional().default(0),
  walkInClosets: z.number().int().min(0).max(9).optional().default(0),
  bathrooms: z.number().int().min(1).max(9),
  sauna: z.boolean().optional().default(false),
  // tallVents (>3.5m) blocks online booking on the website; scaffolding
  // (>4.2m) adds a surcharge. Kept in the schema so the price stays correct
  // if a tall-vents booking is ever submitted directly.
  tallVents: z.boolean().optional().default(false),
  scaffolding: z.boolean().optional().default(false),
  // LTO-only add-ons
  filterChange: z.boolean().optional().default(false),
  greaseDuct: z.boolean().optional().default(false),
  // Non-LTO only: supply-air valve filter count
  supplyAirFilters: z.number().int().min(0).max(9).optional().default(0),
})

export type IlmanvaihtoV2FormValue = z.infer<
  typeof ilmanvaihtoV2FormValueSchema
>

// Hourly rate the duration is derived from (price € → minutes).
const IV_V2_HOURLY_RATE = 125
const IV_V2_LTO_BASE = 250
const IV_V2_OTHER_BASE = 280 // mechanical_exhaust & natural share the same base
const IV_V2_BEDROOM_ADD: Record<number, number> = {
  0: 0,
  1: 35,
  2: 70,
  3: 105,
  4: 140,
  5: 175,
  6: 240,
  7: 310,
}
const IV_V2_WALK_IN_CLOSET = 25
const IV_V2_BATHROOM = 25
const IV_V2_SAUNA = 50
const IV_V2_FLOOR = 40
const IV_V2_SCAFFOLDING = 250
const IV_V2_FILTER_CHANGE = 50
const IV_V2_GREASE_DUCT = 50
const IV_V2_SUPPLY_AIR_FILTER = 15

// Task IDs match production. Main cleaning tasks + cooker-hood are shared with
// the v1 preset; the filter-change task (875026) exists only in prod.
export const IV_V2_FILTER_CHANGE_TASK_ID = 875026

export function computeIlmanvaihtoV2Total(
  formValue: IlmanvaihtoV2FormValue,
): number {
  const {
    ventilationType: ventType,
    floors,
    bedrooms = 0,
    walkInClosets = 0,
    bathrooms,
    sauna = false,
    tallVents = false,
    scaffolding = false,
    filterChange = false,
    greaseDuct = false,
    supplyAirFilters = 0,
  } = formValue
  const isLto = ventType === 'heat_recovery'

  let total = isLto
    ? IV_V2_LTO_BASE + IV_V2_BEDROOM_ADD[bedrooms]
    : IV_V2_OTHER_BASE
  total += walkInClosets * IV_V2_WALK_IN_CLOSET
  total += Math.max(0, bathrooms - 1) * IV_V2_BATHROOM
  if (sauna) total += IV_V2_SAUNA
  total += (floors - 1) * IV_V2_FLOOR
  // >3.5m alone doesn't change the price; scaffolding (>4.2m) does.
  if (tallVents && scaffolding) total += IV_V2_SCAFFOLDING
  if (isLto && filterChange) total += IV_V2_FILTER_CHANGE
  if (isLto && greaseDuct) total += IV_V2_GREASE_DUCT
  if (!isLto) total += supplyAirFilters * IV_V2_SUPPLY_AIR_FILTER

  return Math.max(0, Math.round(total))
}

// Duration = price (minus the filter-change line, which adds no time) converted
// to minutes at the hourly rate, rounded UP to the nearest 15 min, min 15.
export function computeIlmanvaihtoV2DurationMinutes(
  formValue: IlmanvaihtoV2FormValue,
): number {
  const total = computeIlmanvaihtoV2Total(formValue)
  const isLto = formValue.ventilationType === 'heat_recovery'
  const billableForTime =
    total - (isLto && formValue.filterChange ? IV_V2_FILTER_CHANGE : 0)
  const rawMinutes = (billableForTime / IV_V2_HOURLY_RATE) * 60
  return Math.max(15, Math.ceil(rawMinutes / 15) * 15)
}

export function getIlmanvaihtoV2BookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const value = formValue as IlmanvaihtoV2FormValue
  const {
    ventilationType: ventType,
    floors,
    bedrooms = 0,
    walkInClosets = 0,
    bathrooms,
    sauna = false,
    tallVents = false,
    scaffolding = false,
    filterChange = false,
    greaseDuct = false,
    supplyAirFilters = 0,
  } = value
  const isLto = ventType === 'heat_recovery'

  const total = computeIlmanvaihtoV2Total(value)
  const hasFilterChange = isLto && filterChange
  const hasGreaseDuct = isLto && greaseDuct

  // The room/floor/sauna scaling has no dedicated task — it folds into the main
  // cleaning task. Filter change and grease duct are itemized as separate lines.
  const mainTaskPrice =
    total -
    (hasFilterChange ? IV_V2_FILTER_CHANGE : 0) -
    (hasGreaseDuct ? IV_V2_GREASE_DUCT : 0)

  const tasks: ServiceTaskLine[] = [
    { taskId: IV_TASK_IDS[ventType], price: mainTaskPrice },
  ]
  if (hasGreaseDuct) {
    tasks.push({ taskId: IV_TASK_IDS.cookerHood, price: IV_V2_GREASE_DUCT })
  }
  if (hasFilterChange) {
    tasks.push({
      taskId: IV_V2_FILTER_CHANGE_TASK_ID,
      price: IV_V2_FILTER_CHANGE,
    })
  }

  const parts = [`IV-puhdistus, ${IV_TYPE_LABELS[ventType]}`]
  if (floors > 1) parts.push(`${floors} kerrosta`)
  if (isLto && bedrooms > 0) parts.push(`${bedrooms} huonetta`)
  if (walkInClosets > 0) parts.push(`${walkInClosets} vaatehuonetta`)
  if (bathrooms > 1) parts.push(`${bathrooms} wc/kylpyhuonetta`)
  if (sauna) parts.push('sauna')
  if (tallVents)
    parts.push(
      scaffolding ? 'korkeat venttiilit (telineet)' : 'korkeat venttiilit',
    )
  if (hasFilterChange) parts.push('IV-suodattimen vaihto')
  if (hasGreaseDuct) parts.push('rasvahormi')
  if (!isLto && supplyAirFilters > 0)
    parts.push(`${supplyAirFilters} korvausilmasuodatinta`)

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${total} €`,
    estimatedDurationMinutes: computeIlmanvaihtoV2DurationMinutes(value),
    estimatedCost: total,
  }
}

// --- Ilmalämpöpumpun huolto ja syväpuhdistus (heat pump service) preset ---
//
// Flat per-device pricing: the customer picks how many indoor units to clean,
// which sets both the price and the calendar block length. A single
// maintenance task (ILP_HUOLTO_TASK_ID) carries the whole booking; the
// device-count band only changes the line price and duration.
//
// `hasObstacle` is the screening answer (pump in a stairwell / above 2.5 m).
// It needs special equipment, so those bookings are routed to a quote request
// in the widget and never reach this booking flow — the field is kept optional
// here so the form value still validates, but it does not affect pricing.

export const ilpHuoltoFormValueSchema = z.object({
  deviceCount: z.union([z.literal(1), z.literal(2)]),
  hasObstacle: z.boolean().optional(),
})

export type IlpHuoltoFormValue = z.infer<typeof ilpHuoltoFormValueSchema>

interface IlpHuoltoOption {
  deviceCount: number
  duration: number
  cost: number
}

// Task ID matches production — the same ID is seeded locally via fixtures.
export const ILP_HUOLTO_TASK_ID = 62267

// Device count → gross price (€, VAT incl.) and calendar block (min).
export const ILP_HUOLTO_OPTIONS: IlpHuoltoOption[] = [
  { deviceCount: 1, duration: 120, cost: 270 },
  { deviceCount: 2, duration: 180, cost: 390 },
]

export function getIlpHuoltoBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const { deviceCount, hasObstacle } = formValue as IlpHuoltoFormValue
  const opt =
    ILP_HUOLTO_OPTIONS.find((o) => o.deviceCount === deviceCount) ??
    ILP_HUOLTO_OPTIONS[0]

  const tasks: ServiceTaskLine[] = [
    { taskId: ILP_HUOLTO_TASK_ID, price: opt.cost },
  ]

  // Obstacle bookings never reach the calendar flow — they're routed to a
  // quote request, where this serviceDetails is the only signal the back
  // office gets. Spell out the obstacle so it isn't mistaken for a standard,
  // already-priced job (the device-count radio is hidden in that branch, so
  // the price below is just the default and shouldn't be acted on).
  const serviceDetails = hasObstacle
    ? 'Ilmalämpöpumpun huolto ja syväpuhdistus — pumppu portaikossa tai yli 2,5 m korkeudella, vaatii erikoiskaluston (tarjouspyyntö)'
    : `Ilmalämpöpumpun huolto ja syväpuhdistus, ${opt.deviceCount} kpl - ${opt.cost} €`

  return {
    tasks,
    serviceDetails,
    estimatedDurationMinutes: opt.duration,
    estimatedCost: opt.cost,
  }
}

/** Registry of all service presets */
export const SERVICE_PRESET_DATA: Record<ServicePresetKey, ServicePresetData> =
  {
    nuohous: {
      id: 'nuohous',
      name: 'Nuohous',
      description: 'Hormin ja tulisijan nuohous',
      maxDrivingMinutes: 15,
      drivingRelaxation: [
        { daysAhead: 1, extraMinutes: 10 },
        { daysAhead: 2, extraMinutes: 5 },
      ],
      minBookingAdvanceHours: 4,
      slotSpacingMinutes: 45,
      notificationEmails: ['info@markku.fi'],
      defaultFormValue: {
        fireplaces: 1,
        hasBoiler: false,
      } satisfies NuohousFormValue as Record<string, unknown>,
      getBookingData: getNuohousBookingData,
    },
    ilmanvaihto: {
      id: 'ilmanvaihto',
      name: 'IV-kanavien puhdistus (vanha)',
      description: 'Ilmanvaihtokanavien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotSpacingMinutes: 240,
      notificationEmails: ['info@markku.fi', 'jani.issakainen@markku.fi'],
      defaultFormValue: {
        apartmentSize: 'under_80',
        floors: 'single',
        ventilationType: 'natural',
        includeAirflowMeasurement: false,
        includeCookerHoodCleaning: false,
      } satisfies IlmanvaihtoFormValue as Record<string, unknown>,
      getBookingData: getIlmanvaihtoBookingData,
    },
    ilmanvaihto_v2: {
      id: 'ilmanvaihto_v2',
      name: 'IV-kanavien puhdistus',
      description: 'Ilmanvaihtokanavien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotSpacingMinutes: 240,
      notificationEmails: ['info@markku.fi', 'jani.issakainen@markku.fi'],
      defaultFormValue: {
        ventilationType: 'heat_recovery',
        floors: 1,
        bedrooms: 0,
        walkInClosets: 0,
        bathrooms: 1,
        sauna: false,
        tallVents: false,
        scaffolding: false,
        filterChange: false,
        greaseDuct: false,
        supplyAirFilters: 0,
      } satisfies IlmanvaihtoV2FormValue as Record<string, unknown>,
      getBookingData: getIlmanvaihtoV2BookingData,
    },
    ilp_huolto: {
      id: 'ilp_huolto',
      name: 'Ilmalämpöpumpun huolto ja syväpuhdistus',
      description: 'Ilmalämpöpumpun huolto ja syväpuhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      // Start-to-start spacing of offered slots. 150 min yields the desired
      // 8:00 / 10:30 / 13:00 cadence when the availability window starts at 8:00.
      slotSpacingMinutes: 150,
      notificationEmails: ['info@markku.fi'],
      defaultFormValue: {
        deviceCount: 1,
        hasObstacle: false,
      } satisfies IlpHuoltoFormValue as Record<string, unknown>,
      getBookingData: getIlpHuoltoBookingData,
    },
  }
